import { z } from "zod";
import { staffImportBackgroundTaskIds, staffImportIntentStates } from "./staff-imports.js";
// The contract of the staff operations jobs routes (CHT-761; docs/architecture/milestone-e-jobs-contract.md section 4.3):
//
//   GET  /staff/operations/jobs                  the list of background intents (capability `operations.jobs.read`)
//   GET  /staff/operations/jobs/summary          counts per task and state, the age of the oldest due intent, the controls
//   POST /staff/operations/jobs/:intentId/requeue  re-run a FAILED or CANCELLED intent (capability `operations.jobs.manage`)
//   PUT  /staff/operations/job-controls/:taskId  pause or resume one task, or every task with `*` (capability `operations.jobs.manage`)
//
// Every shape is STRICT: the API validates a body with these schemas, and the admin and the typed client validate the response with
// them, so a key that is not listed fails the parse. The view has NO idempotency key, no payload and no free text: ids, closed codes,
// counts and times only. This package cannot import the domain package: the tuples below repeat the domain's
// (`background-jobs.ts`); `apps/api` has a parity test that fails when one differs.
// --- codes (parity-tested against the domain) ------------------------------------------------------
export const staffOperationsTaskIds = staffImportBackgroundTaskIds;
export const staffOperationsIntentStates = staffImportIntentStates;
export const staffOperationsSubjectTypes = [
    "import_job",
    "catalog_item",
    "memory_extraction_run",
    "conversation",
    "handoff",
    "staff_user",
    "concierge_request",
    "payment_request",
    "notification_event",
    "trip",
    "itinerary_item",
    "privacy_request",
];
export const staffOperationsRunners = ["worker", "api", "in-process"];
export const staffOperationsCancelReasons = [
    "SUBJECT_CANCELLED",
    "SUBJECT_DELETED",
    "SUPERSEDED",
    "OPERATOR",
];
export const staffOperationsDecisionCodes = [
    "ACTED",
    "SUPERSEDED",
    "SUPPRESSED_RESOLVED",
    "SUPPRESSED_PREFERENCE",
    "SUPPRESSED_DUPLICATE",
    "SKIPPED_PROVIDER_UNAVAILABLE",
    "SKIPPED_PAUSED",
    "RETRY_EXHAUSTED",
    "NEEDS_RECONCILIATION",
];
export const staffOperationsErrorClasses = [
    "RETRYABLE",
    "RATE_LIMITED",
    "AMBIGUOUS",
    "TERMINAL",
];
export const staffOperationsControlReasons = [
    "INCIDENT",
    "PROVIDER_OUTAGE",
    "COST",
    "MAINTENANCE",
    "PROOF",
];
export const staffOperationsWorkloadClasses = ["critical", "interactive", "batch"];
/** The target of a control row that stops every task. */
export const STAFF_OPERATIONS_ALL_TASKS = "*";
export const STAFF_OPERATIONS_JOBS_BOUNDS = Object.freeze({
    /** `BACKGROUND_INTENT_LIST_DEFAULT_LIMIT` */
    defaultLimit: 50,
    /** `BACKGROUND_INTENT_LIST_MAX_LIMIT` */
    maxLimit: 200,
    cursor: 100,
});
export const STAFF_OPERATIONS_JOBS_PATH = "/staff/operations/jobs";
export const STAFF_OPERATIONS_JOBS_SUMMARY_PATH = "/staff/operations/jobs/summary";
export const STAFF_OPERATIONS_JOBS_REQUEUE_PATH = "/staff/operations/jobs/:intentId/requeue";
export const STAFF_OPERATIONS_JOB_CONTROL_PATH = "/staff/operations/job-controls/:taskId";
// --- shared pieces -----------------------------------------------------------------------------------
const count = z.number().int().min(0);
const responseInstant = z.union([
    z.date(),
    z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
]);
const nullableResponseInstant = responseInstant.nullable();
const id = z.guid();
// --- request ----------------------------------------------------------------------------------------
/** A comma list in a query value: `FAILED,CANCELLED`. Anything outside the closed list is refused. */
const commaList = (values) => z
    .string()
    .min(1)
    .max(400)
    .transform((value) => value.split(",").map((part) => part.trim()))
    .pipe(z.array(z.enum(values)).min(1).max(values.length));
export const staffOperationsJobsQuerySchema = z.strictObject({
    state: commaList(staffOperationsIntentStates).optional(),
    task: commaList(staffOperationsTaskIds).optional(),
    subjectId: id.transform((value) => value.toLowerCase()).optional(),
    updatedSince: z.iso
        .datetime({ offset: true })
        .transform((value) => new Date(value))
        .optional(),
    cursor: z
        .string()
        .min(1)
        .max(STAFF_OPERATIONS_JOBS_BOUNDS.cursor)
        .regex(/^[0-9TZ:.|a-f-]+$/)
        .optional(),
    limit: z.coerce.number().int().min(1).max(STAFF_OPERATIONS_JOBS_BOUNDS.maxLimit).optional(),
});
export const staffOperationsRequeueBodySchema = z.strictObject({
    reasonCode: z.enum(staffOperationsControlReasons),
});
/** Pause needs a reason; resume names none. */
export const staffOperationsControlBodySchema = z.discriminatedUnion("paused", [
    z.strictObject({ paused: z.literal(true), reasonCode: z.enum(staffOperationsControlReasons) }),
    z.strictObject({ paused: z.literal(false) }),
]);
export const staffOperationsControlTaskSchema = z.enum([
    ...staffOperationsTaskIds,
    STAFF_OPERATIONS_ALL_TASKS,
]);
// --- response ----------------------------------------------------------------------------------------
export const staffOperationsIntentSchema = z.strictObject({
    id,
    taskId: z.enum(staffOperationsTaskIds),
    taskVersion: count,
    subjectType: z.enum(staffOperationsSubjectTypes),
    subjectId: id,
    state: z.enum(staffOperationsIntentStates),
    attempts: count,
    maxAttempts: count,
    nextAttemptAt: responseInstant,
    lastErrorCode: z.string().nullable(),
    errorClass: z.enum(staffOperationsErrorClasses).nullable(),
    lastRunner: z.enum(staffOperationsRunners).nullable(),
    lastRunRef: z.string().nullable(),
    cancelReason: z.enum(staffOperationsCancelReasons).nullable(),
    createdAt: responseInstant,
    updatedAt: responseInstant,
    completedAt: nullableResponseInstant,
    latestDecision: z
        .strictObject({
        code: z.enum(staffOperationsDecisionCodes),
        reasonCode: z.string().nullable(),
    })
        .nullable(),
});
export const staffOperationsIntentPageSchema = z.strictObject({
    items: z.array(staffOperationsIntentSchema).max(STAFF_OPERATIONS_JOBS_BOUNDS.maxLimit),
    nextCursor: z.string().nullable(),
});
export const staffOperationsTaskSummarySchema = z.strictObject({
    taskId: z.enum(staffOperationsTaskIds),
    workloadClass: z.enum(staffOperationsWorkloadClasses),
    taskVersion: count,
    counts: z.strictObject({
        PENDING: count,
        CLAIMED: count,
        SUCCEEDED: count,
        FAILED: count,
        CANCELLED: count,
    }),
    oldestDueAgeSeconds: count.nullable(),
    paused: z.boolean(),
});
export const staffOperationsControlSchema = z.strictObject({
    taskId: staffOperationsControlTaskSchema,
    paused: z.boolean(),
    reasonCode: z.enum(staffOperationsControlReasons).nullable(),
    changedBy: id.nullable(),
    changedAt: responseInstant,
});
export const staffOperationsSummarySchema = z.strictObject({
    generatedAt: responseInstant,
    tasks: z.array(staffOperationsTaskSummarySchema).max(staffOperationsTaskIds.length),
    controls: z.array(staffOperationsControlSchema).max(staffOperationsTaskIds.length + 1),
});
export const staffOperationsRequeueResultSchema = z.strictObject({
    taskId: z.enum(staffOperationsTaskIds),
    oldIntentId: id,
    newIntentId: id,
});
export const staffOperationsControlResultSchema = z.strictObject({
    taskId: staffOperationsControlTaskSchema,
    paused: z.boolean(),
    changed: z.boolean(),
});
